import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import PublicNavbar from '../components/public/PublicNavbar';
import LoginHero, { HeroMascot } from '../components/public/LoginHero';
import AuthCard from '../components/public/AuthCard';
import HowItWorks from '../components/public/HowItWorks';
import FeatureCallouts from '../components/public/FeatureCallouts';
import ProductPreview from '../components/public/ProductPreview';
import PricingCards from '../components/public/PricingCards';
import FinalCta from '../components/public/FinalCta';
import { AudienceSection, SpotlightSection, LearnTeaser, FaqSection, PublicFooter } from '../components/public/landingSections';
import type { PublicPlan } from '../services/billing';
import authService from '../services/auth';

const Login: React.FC = () => {
  const [plans, setPlans] = React.useState<PublicPlan[] | null>(null);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    let cancelled = false;
    import('../services/billing')
      .then(({ default: billingService }) => billingService.catalog())
      .then((rows) => {
        if (!cancelled) setPlans(rows);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authService.getToken()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        overflowX: 'hidden',
        minHeight: '100vh',
        bgcolor: 'background.default',
        backgroundImage: (theme) =>
          theme.palette.mode === 'dark'
            ? 'radial-gradient(900px 420px at 10% -10%, rgba(245,197,24,0.14), transparent 60%), linear-gradient(180deg, rgba(22,58,95,0.55), transparent 480px)'
            : 'radial-gradient(900px 420px at 12% -8%, rgba(245,197,24,0.28), transparent 55%), linear-gradient(180deg, rgba(11,31,58,0.08), transparent 520px)',
      }}
    >
      <PublicNavbar />
      <Box component="main" sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 5 }, maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.92fr) minmax(300px, 1.08fr)' },
            gap: { xs: 3, md: 3 },
            alignItems: 'center',
            mb: { xs: 4, md: 6 },
          }}
        >
          <Box>
            <LoginHero />
            <Box sx={{ display: { xs: 'block', md: 'none' }, my: 3 }}>
              <HeroMascot />
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <ProductPreview />
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 2 }}>
              <HeroMascot />
            </Box>
            <ProductPreview cornerInset />
          </Box>
        </Box>
        <Box id="sign-in-wrap" sx={{ maxWidth: 480, mb: { xs: 6, md: 8 } }}>
          <AuthCard />
        </Box>
        <Box sx={{ display: 'grid', gap: { xs: 6, md: 9 } }}>
          <HowItWorks />
          <FeatureCallouts />
          <AudienceSection />
          <SpotlightSection />
          <LearnTeaser />
          {plans && plans.length > 0 && <PricingCards plans={plans} />}
          <FaqSection />
          <FinalCta />
          <PublicFooter />
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
